import type { Reference } from '@/features/sheets/contract/schema';

export type ScopeAgentMode = 'run' | 'update';

export type ScopeStructure =
  | { kind: 'library' }
  | { kind: 'volve' }
  | { kind: 'template'; documentIds: string[] }
  | { kind: 'tender_bill' };

export type ScopeStructureKind = ScopeStructure['kind'];

export type StartScopeAgentRun = {
  startMode?: ScopeStartMode;
  profileId?: string;
  mode: ScopeAgentMode;
  structure?: ScopeStructure | null;
  knowledgeBaseIds?: string[];
  instructions?: string | null;
};

type FileCoverage = {
  docId: string;
  filename: string;
  state: 'cited' | 'no_scope' | 'not_read' | 'open';
  reason: string | null;
};

export type ScopeRunEnding =
  | 'time_limit'
  | 'allowance'
  | 'answer_refused'
  | 'agent_failed'
  | 'no_standard'
  | 'expired'
  | 'crashed'
  | 'invalid_result'
  | 'invalid_source';

export type ScopeAgentOutcome = {
  message: string;
  answerStatus: 'completed' | 'partial';
  docIds: string[];
  uncovered: FileCoverage[];
  noScope: number;
  usage: Record<string, number>;
  reason: ScopeRunEnding | null;
};

export type ScopeRunPhase = 'planning' | 'reading' | 'measuring' | 'trades' | 'lead';

export type ScopeTaskActivity = {
  kind: string;
  label: string;
  startedAt: string;
  finishedAt: string | null;
  status: string | null;
  error?: string | null;
};

export type ScopeTabActivity = {
  tab: string;
  posts: number;
  withQuantity: number;
  assumptions: number;
  inputNeeded: number;
};

export type ScopeRunActivity = {
  startedAt: string;
  lastActivityAt: string;
  costUsd: number;
  tasks: ScopeTaskActivity[];
  tabs: ScopeTabActivity[];
  phase: ScopeRunPhase;
};

export type ScopeAgentRunView = {
  id: string;
  mode: ScopeAgentMode;
  state: 'running' | 'done' | 'failed';
  progress: number;
  artifactId: string | null;
  structure: ScopeStructure | null;
  knowledgeBaseIds: string[];
  instructions: string | null;
  language: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  outcome: ScopeAgentOutcome | null;
  reason: ScopeRunEnding | null;
  activity?: ScopeRunActivity | null;
  errorMessage?: string | null;
};

export type ScopeRowCells = {
  code: string | null;
  kind: 'post' | 'note' | 'intro' | null;
  title: string | null;
  scope: string | null;
  description: string | null;
  quantity: number | null;
  unit: string | null;
  location: string | null;
  package: string | null;
  discipline: string | null;
  status: 'included' | 'excluded' | 'by_others' | 'clarify' | null;
  sources: Reference[];
};

export type ScopeRow = ScopeRowCells & {
  workPackageId?: string | null;
  id: string;
  tabId: string;
  tab: string;
  parentId: string | null;
  group: boolean;
};

export const SCOPE_CONTRACT_PLACEMENT = {
  code: 'line',
  kind: 'meta',
  title: 'line',
  scope: 'line',
  description: 'meta',
  quantity: 'line',
  unit: 'line',
  location: 'field',
  package: 'meta',
  discipline: 'meta',
  status: 'line',
  sources: 'line',
} as const satisfies Record<keyof ScopeRowCells, 'line' | 'field' | 'meta'>;

export type KnowledgeBaseChoice = { id: string; name: string };

export type ScopeAgentStatus = {
  available?: boolean;
  evaluationId: string;
  artifactId: string | null;
  run: ScopeAgentRunView | null;
  knowledgeBases: KnowledgeBaseChoice[];
  hasTenderBill: boolean;
};

export enum ScopeAgentRunStatus {
  DONE = 20,
  FAILED = 30,
}

export type ScopeRunForm = {
  mode: ScopeAgentMode;
  structure: ScopeStructureKind | null;
  documentIds: string[];
  knowledgeBaseIds: string[];
  instructions: string;
};

export type ScopeStartMode = 'requirements' | 'client_posts';
export type ScopeMethodFamily = 'performance' | 'itemised';
export type ScopeExecution = 'self_perform' | 'subcontract';
export type ScopePackageStep =
  | 'client-posts'
  | 'changes'
  | 'requirements'
  | 'choose-description'
  | 'work-pack'
  | 'add-information'
  | 'documents'
  | 'generate'
  | 'proofread'
  | 'send'
  | 'offers';

export type ScopeStandardProfile = {
  id: string;
  name: string;
  sources?: { documentId: string | null }[];
  editions?: string[];
  descriptionMethods: {
    key: string;
    localName: string;
    family: ScopeMethodFamily;
    standardLine: string | null;
    whenUsed: string;
  }[];
  terms: { key: string; singular: string; plural: string }[];
  chapters: { key: string; title: string; parentKey: string | null }[];
  lineLayout: { key: string; label: string; options: string[] }[];
  exchangeFormats: { key: string; name: string; extension: string }[];
  subcontractForms: string[];
  subcontractMainContracts: Record<string, string>;
  packageDocumentSections: { key: string; title: string; instructions: string }[];
};

export type ScopeSetupFile = {
  id: string;
  name: string;
  pages: number | null;
  status: 'processing' | 'ready' | 'failed';
};

export type ScopeStandardSetup = {
  profiles: { profile: ScopeStandardProfile; revision: number }[];
  setup: {
    profileId: string;
    perspective: 'contractor' | 'client';
    structure: ScopeStructure;
    knowledgeBaseIds: string[];
  } | null;
  files: ScopeSetupFile[];
  referenceFiles: (ScopeSetupFile & { knowledgeBaseId?: string | null })[];
  perspective: 'contractor' | 'client';
};

export type ScopePackage = {
  id: string;
  displayName: string;
  sourceKey: string | null;
  kind: 'normal' | 'rest' | 'all' | 'removed';
  origin: 'standard_chapter' | 'client_chapter' | 'outside_standard' | 'user' | 'template';
  position: number;
  execution: ScopeExecution | null;
  methodKey: string | null;
  contractStandard: string | null;
  lineStandardDocumentIds: string[];
  quantityMode: 'check_first' | 'auto';
  requirementsCheckedAt: string | null;
  status: 'draft' | 'ready' | 'sent' | 'offers_in' | 'awarded' | 'contracted';
  version: number;
  hasDescription: boolean;
  descriptionState?: ScopeDescription['state'] | null;
  roleWording: ScopeRoleWording;
};

export type ScopeRoleWording = 'rewrite' | 'intro_note';

export type ScopeApiError = {
  code: string;
  message: string;
  documentName?: string | null;
};

export type ScopeRequirement = {
  id: string;
  label?: string;
  displayNo: string;
  text: string;
  displayText?: string;
  userText: string | null;
  workPackageId: string;
  clientOrder: number;
  origin: 'user' | 'agent' | 'bill';
  primaryCitationId: string | null;
  headingPath: string[];
  quantity: number | null;
  unit: string | null;
  status: 'included' | 'excluded' | 'by_others' | 'clarify';
  flags: (
    | 'gap'
    | 'source_changed'
    | 'source_removed'
    | 'user_edited'
    | 'outside_standard'
    | 'uncertain_placement'
    | 'overlap'
    | 'conflict'
    | 'clarify'
  )[];
};

export type ScopeBreakdown = {
  breakdown: {
    id: string;
    standardProfileId: string | null;
    perspective: 'contractor' | 'client';
    startMode: ScopeStartMode;
    revision: number;
  };
  packages: ScopePackage[];
  items: ScopeRequirement[];
  citations: ScopeCitation[];
  conditions: ScopeCondition[];
  pricedLines: ScopeLine[];
  extraction: {
    clientChapters: {
      key: string;
      title: string;
      documentId: string;
      citationId?: string;
      workPackageId: string;
      commonPart: boolean;
    }[];
    mergeSuggestions: { packageKeys: string[]; reason: string }[];
  };
};

export type ScopeLevel = 'exact' | 'paragraph' | 'section' | 'chapter' | 'document' | 'manual';
export type ScopeWordBox = { page: number; x0: number; y0: number; x1: number; y1: number };
export type ScopeSpan = {
  sourceFormat?: 'paginated' | 'unpaginated';
  pageFrom: number;
  pageTo: number;
  boxes: ScopeWordBox[];
};
export type ScopeCitationSpan = Omit<ScopeSpan, 'boxes'>;
export type ScopeCitation = {
  id: string;
  displayNo: string;
  documentId: string;
  exactText: string;
  recordKey?: string | null;
  span: ScopeCitationSpan;
  effectiveSpan: ScopeCitationSpan;
  granularity: ScopeLevel;
  contractGranularity: ScopeLevel;
  expansionIds: Partial<Record<ScopeLevel, string>>;
};
export type ScopePreviewCitation = Omit<ScopeCitation, 'span' | 'effectiveSpan'> & {
  span: ScopeSpan;
  effectiveSpan: ScopeSpan;
};
export type ScopeCondition = {
  id: string;
  displayNo: string;
  text: string;
  category: string;
  packageIds: string[];
  appliesTo: 'all' | 'packages' | 'main_contract';
  primaryCitationId: string | null;
};
export type ScopeSource = {
  field: string;
  reference: string;
  documentId: string | null;
  page: number | null;
  quote?: string | null;
  span?: ScopeSpan | null;
};
export type ScopeLine = {
  id: string;
  clientNumber?: string | null;
  workPackageId: string;
  code: string;
  title: string;
  fields: {
    key: string;
    value: string;
    label?: string | null;
    options?: string[];
    emphasis?: boolean;
    kind?: 'matrix' | 'free' | 'numeric';
    status?: ScopeFieldStatus;
    suggestedValue?: string | null;
    unit?: string | null;
  }[];
  summary: string;
  quantity: number | null;
  unit: string | null;
  unitPrice?: number | null;
  quantityBasis: 'stated' | 'derived' | 'measured' | 'assumed' | 'none' | null;
  quantityNote?: string | null;
  coverage?: { itemId: string; summary: string }[];
  codeVariants?: { code: string; choices: { fieldKey: string; value: string }[] }[];
  isComposed: boolean;
  childItemIds: string[];
  primaryItemIds: string[];
  chapterKey: string;
  location: string;
  isRigging: boolean;
  riggingSourceLineId?: string | null;
  origin: 'user' | 'agent' | 'bill';
  sourceFileId: string | null;
  originalLineId: string | null;
  removed: boolean;
  sources: ScopeSource[];
  notes: {
    fieldKey: string;
    kind: 'assumption' | 'user_must_select' | 'input_needed';
    text: string;
    options: string[];
  }[];
};
export type ScopeFieldStatus = 'known' | 'assumption' | 'input_needed';
export type ScopeTakeoff = {
  summary: string;
  measurements: {
    id: string;
    label: string;
    itemIds: string[];
    quantity: number | null;
    unit: string;
    basis: 'stated' | 'derived' | 'measured' | 'assumed' | 'none';
    calculation: string;
    assumptions: string[];
    sources: ScopeSource[];
  }[];
  files: { id: string; name: string; blob: string }[];
  acceptedIds: string[];
  usage: { costUsd: number; containerCostUsd: number };
};
export type ScopeDescription = {
  operation?: 'generate' | 'takeoff';
  takeoff?: ScopeTakeoff | null;
  workPackageId: string;
  version: number;
  state: 'draft' | 'queued' | 'generating' | 'generated' | 'failed';
  summary: string;
  introduction: string;
  introductionSources: ScopeSource[];
  sections: {
    key: string;
    title: string;
    text: string;
    itemIds: string[];
    sources: ScopeSource[];
  }[];
  lines: ScopeLine[];
  interfaces: string[];
  deliveryMatrix: { chapter: string; activity: string; responsible: string }[];
  uncoveredItemIds: string[];
  missingDocuments: string[];
  generatedAt: string | null;
  progress?: {
    stage:
      | 'queued'
      | 'reading'
      | 'summary'
      | 'interfaces'
      | 'conditions'
      | 'lines'
      | 'quantities'
      | 'finishing';
    percent: number;
    queuePosition: number | null;
    pendingItemIds: string[];
  } | null;
  error?: ScopeApiError | null;
  attachments?: ScopeAttachment[];
};
export type ScopeAttachment = { name: string; label: string };
type ScopePreviewRow = { id: string; displayNo: string; text: string; page: number | null };
export type ScopePackagePreview = {
  title: string;
  method: { localName: string } | null;
  summary: string;
  introduction: string;
  interfaces: string[];
  attachments: ScopeAttachment[];
  sections: {
    key: string;
    title: string;
    text: string;
    items: ScopePreviewRow[];
    lines: ScopeLine[];
  }[];
  conditions: ScopePreviewRow[];
};
export type ScopeSectionGeneration = {
  sectionKey: string;
  state: ScopePackageGeneration['state'];
  error: ScopeApiError | null;
  operationId: string | null;
};
export type ScopePackageDocument = {
  id: string;
  workPackageId: string;
  version: number;
  sectionKey: string;
  position: number;
  state: 'uploaded' | 'generated' | 'not_relevant' | 'empty';
  fileIds: string[];
  origin: 'user' | 'agent' | 'bill';
  title: string;
  text: string;
  inputHash: string | null;
  suggestedDrawings: { docId: string; first: number; last: number; reason: string }[];
};
export type ScopeProofreadCheck = {
  id: string;
  workPackageId: string;
  version: number;
  location: string;
  text: string;
  source: ScopeSource | null;
  suggestedFix: string;
  actionLabel: string;
  state: 'open' | 'applied' | 'ignored';
};

export type ScopeClientChange = {
  lineId: string | null;
  itemId: string | null;
  kind: 'added' | 'edited' | 'moved' | 'removed' | 'condition_added';
  original: Pick<ScopeLine, 'code' | 'title' | 'quantity' | 'unit' | 'fields'> | null;
  current: ScopeLine | null;
  text: string;
};

export type ScopePackageFile = {
  id: string;
  name: string;
  kind: 'document' | 'page_bundle' | 'merged';
  sectionKey: string | null;
  formatKey: string | null;
  mediaType: string;
  contentHash: string;
  size: number;
};
export type ScopePackageGeneration = {
  operationId: string | null;
  inputHash: string;
  workPackageId: string;
  version: number;
  state: 'dispatching' | 'running' | 'retrying' | 'unknown' | 'succeeded' | 'failed' | 'aborted';
  progress?: {
    stage: 'writing' | 'sources' | 'files' | 'page_bundle' | 'merging' | 'complete';
    completedFiles: number;
    totalFiles: number | null;
  } | null;
  request?: ScopeGeneratePackageRequest | null;
  error?: ScopeApiError | null;
  snapshot: {
    id: string;
    version: number;
    inputHash: string | null;
    files: ScopePackageFile[];
    pages: { documentId: string; page: number }[];
  } | null;
};
export type ScopeGeneratePackageRequest = {
  kind: 'section' | 'package';
  changeLogFormatKey?: string;
  sectionKey?: string;
  files?: { sectionKey: string; formatKey: string | null }[];
  drawings?: { docId: string; first: number; last: number }[];
};
